import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUp, MessageCircle, X } from "lucide-react";
import { EASE } from "./Motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUGGESTIONS = ["What do you build?", "Where do you work?", "Who is Matt Newland?", "How do estimates work?"];
const GREETING = { role: "assistant", content: "Hi! I'm the Newland assistant. Ask me about decks, covered porches, siding, our service area or how estimates work." };

const sessionId = () => {
  let id = localStorage.getItem("newland_chat_session");
  if (!id) { id = `s-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`; localStorage.setItem("newland_chat_session", id); }
  return id;
};

const streamChat = async (message, onDelta) => {
  const res = await fetch(`${API}/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: sessionId(), message }),
  });
  if (!res.ok) throw new Error("chat failed");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const events = buf.split("\n\n");
    buf = events.pop();
    for (const ev of events) {
      const data = ev.replace(/^data: /, "");
      if (data === "[DONE]") return;
      const j = JSON.parse(data);
      if (j.error) throw new Error(j.error);
      onDelta(j.delta);
    }
  }
};

const Bubble = ({ m }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
    <div data-testid={`chat-message-${m.role}`}
      className={`max-w-[85%] whitespace-pre-wrap px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "rounded-2xl rounded-br-sm bg-white text-[#0A0A0A]" : "rounded-2xl rounded-bl-sm border border-white/10 bg-[#161616] text-neutral-200"}`}>
      {m.content || <span className="inline-flex gap-1"><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-neutral-400" /><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-neutral-400 [animation-delay:.15s]" /><span className="h-1.5 w-1.5 animate-bounce rounded-full bg-neutral-400 [animation-delay:.3s]" /></span>}
    </div>
  </div>
);

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([GREETING]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const list = useRef(null);

  useEffect(() => {
    const f = () => setOpen(true);
    window.addEventListener("open-chat", f);
    return () => window.removeEventListener("open-chat", f);
  }, []);

  useEffect(() => { list.current?.scrollTo({ top: list.current.scrollHeight }); }, [msgs, open]);

  const send = async (q) => {
    const message = q.trim();
    if (!message || busy) return;
    setText("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    const append = (d) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + d }; return c; });
    try {
      await streamChat(message, append);
    } catch {
      setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "Sorry, I'm having trouble right now. Please use the estimate form and Matt will get back to you." }; return c; });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {!open && (
          <motion.button data-testid="chat-open-button" onClick={() => setOpen(true)}
            initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }} transition={{ duration: 0.4, ease: EASE }}
            className="group fixed bottom-5 right-5 z-[80] flex items-center gap-3 rounded-full bg-white p-2.5 sm:py-3 sm:pl-4 sm:pr-5 text-sm font-semibold text-[#0A0A0A] shadow-[0_10px_40px_rgba(0,0,0,0.5)] md:bottom-8 md:right-8">
            <span className="relative flex h-7 w-7 items-center justify-center rounded-full bg-[#0A0A0A] text-white">
              <MessageCircle className="h-3.5 w-3.5" />
              <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-emerald-500" />
            </span>
            <span className="hidden sm:inline">Ask Newland</span>
          </motion.button>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {open && (
          <motion.div data-testid="chat-panel"
            initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 30, scale: 0.97 }} transition={{ duration: 0.45, ease: EASE }}
            className="fixed inset-x-3 bottom-3 z-[80] flex h-[min(620px,calc(100svh-24px))] flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0D0D0D]/95 shadow-[0_30px_80px_rgba(0,0,0,0.6)] backdrop-blur-xl md:inset-x-auto md:bottom-8 md:right-8 md:w-[400px]">
            <div className="flex items-center justify-between border-b border-white/[0.08] px-5 py-4">
              <div className="flex items-center gap-3">
                <img src="/brand/newland-roofs-light.png" alt="" className="h-5 w-auto" />
                <div>
                  <div className="font-display text-base font-bold text-white">Ask Newland</div>
                  <div className="flex items-center gap-1.5 font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />AI assistant</div>
                </div>
              </div>
              <button data-testid="chat-close-button" onClick={() => setOpen(false)} className="rounded-full border border-white/10 p-2 text-neutral-300 hover:text-white" aria-label="Close chat">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div ref={list} data-lenis-prevent className="flex-1 space-y-3 overflow-y-auto px-4 py-5">
              {msgs.map((m, i) => <Bubble key={i} m={m} />)}
              {msgs.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {SUGGESTIONS.map((s, i) => (
                    <button key={s} data-testid={`chat-suggestion-${i}`} onClick={() => send(s)}
                      className="rounded-full border border-white/15 px-3 py-1.5 text-xs text-neutral-300 transition-colors hover:border-white hover:text-white">{s}</button>
                  ))}
                </div>
              )}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(text); }} className="flex items-center gap-2 border-t border-white/[0.08] p-3">
              <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} placeholder="Ask about decks, porches, siding..."
                className="flex-1 rounded-full border border-white/10 bg-[#161616] px-4 py-3 text-sm text-white placeholder:text-neutral-500 focus:border-white/40 focus:outline-none" />
              <button data-testid="chat-send-button" type="submit" disabled={busy || !text.trim()} aria-label="Send"
                className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#0A0A0A] transition-opacity disabled:opacity-40">
                <ArrowUp className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
